import React from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Upload,
  Eye,
  Users,
  Layers,
  LogOut,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  currentPage,
  setCurrentPage,
  onViewResumes,
}) => {
  const { logout, user } = useAuth();

  return (
    <>
      {/* ─────────────────────── */}
      {/* COLLAPSED SIDEBAR      */}
      {/* ─────────────────────── */}
      {!sidebarOpen && (
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="
            fixed top-0 left-0 h-full w-14 
            bg-white/60 backdrop-blur-md border-r border-gray-200
            shadow-lg flex flex-col items-center py-4 
            space-y-7 z-50"
        >
          {/* Expand Button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md hover:bg-gray-100 text-gray-700 transition"
          >
            <ChevronRight size={22} />
          </button>

          {/* Nav Icons */}
          <div className="flex flex-col items-center space-y-7 mt-4">

            <CollapsedIcon
              icon={<Plus size={20} />}
              title="Add Role"
              onClick={() => setCurrentPage("admin")}
              active={currentPage === "admin"}
            />

            <CollapsedIcon
              icon={<Trash2 size={20} />}
              title="Existing Roles"
              onClick={() => setCurrentPage("existing-roles")}
              active={currentPage === "existing-roles"}
            />

            <CollapsedIcon
              icon={<Upload size={20} />}
              title="Upload Resumes"
              onClick={() => setCurrentPage("upload")}
              active={currentPage === "upload"}
            />

            <CollapsedIcon
              icon={<Eye size={20} />}
              title="View Resumes"
              onClick={onViewResumes}
            />

            <CollapsedIcon
              icon={<Users size={20} />}
              title="Recruiter Agent"
              onClick={() => setCurrentPage("recruiter")}
              active={currentPage === "recruiter"}
            />
          </div>

          {/* Logout Icon at Bottom */}
          <div className="mt-auto">
            <CollapsedIcon
              icon={<LogOut size={20} />}
              title="Sign Out"
              onClick={logout}
            />
          </div>
        </motion.div>
      )}

      {/* ─────────────────────── */}
      {/* EXPANDED SIDEBAR        */}
      {/* ─────────────────────── */}
      {sidebarOpen && (
        <>
          {/* Mobile backdrop overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-black/30 z-40 md:hidden"
          />
          <motion.aside
            initial={{ x: -260 }}
            animate={{ x: 0 }}
            exit={{ x: -260 }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }}
            className="
              fixed top-0 left-0 h-full w-64 sm:w-72 
              bg-white/95 md:bg-white/75 backdrop-blur-xl 
              border-r border-gray-200 shadow-xl 
              flex flex-col z-50"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Layers size={24} className="text-orange-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Admin Panel
                </h2>
              </div>

              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-700"
              >
                <ChevronLeft size={24} />
              </button>
            </div>

            {/* Menu Items */}
            <nav className="p-5 space-y-4 flex-1">
              <SidebarButton
                icon={<Plus size={20} />}
                label="Add Role"
                active={currentPage === "admin"}
                onClick={() => setCurrentPage("admin")}
              />

              <SidebarButton
                icon={<Trash2 size={20} />}
                label="Existing Roles"
                active={currentPage === "existing-roles"}
                onClick={() => setCurrentPage("existing-roles")}
              />

              <SidebarButton
                icon={<Upload size={20} />}
                label="Upload Resumes"
                active={currentPage === "upload"}
                onClick={() => setCurrentPage("upload")}
              />

              <SidebarButton
                icon={<Eye size={20} />}
                label="View Resumes"
                onClick={onViewResumes}
              />

              <SidebarButton
                icon={<Users size={20} />}
                label="Recruiter Agent"
                active={currentPage === "recruiter"}
                onClick={() => setCurrentPage("recruiter")}
              />
            </nav>

            {/* User Info & Logout at Bottom */}
            <div className="border-t border-gray-100 p-4">
              {user && (
                <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-gradient-to-r from-orange-50 to-orange-100/70">
                  {/* Avatar with Green Status Dot */}
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold text-lg shadow-md">
                      {(user.name || user.email || user.username)?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-800 font-semibold text-sm leading-tight">
                      {user.name || user.email || user.username}
                    </span>
                    <span className="text-xs text-orange-600 font-medium leading-tight">
                      Administrator
                    </span>
                  </div>
                </div>
              )}

              {/* Sign Out Button - Orange Style */}
              <button
                onClick={logout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-md hover:shadow-lg transition-all active:scale-95"
                title="Sign Out"
              >
                <LogOut size={18} />
                <span>Sign Out</span>
              </button>

              {/* Copyright Footer */}
              <p className="text-center text-xs text-gray-400 mt-4">
                © 2026 Creyente InfoTech
              </p>
            </div>
          </motion.aside>
        </>
      )}
    </>
  );
};

/* ────────────────────────────── */
/* REUSABLE COMPONENTS            */
/* ────────────────────────────── */

// Expanded button style
const SidebarButton = ({ icon, label, active, onClick }) => (
  <motion.button
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    onClick={onClick}
    className={`
      relative w-full flex items-center gap-3 px-4 py-3 rounded-xl 
      font-medium transition-all
      ${active ? "bg-orange-600 text-white shadow-lg" : "bg-white/70 hover:bg-gray-100 text-gray-800"}
    `}
  >
    {active && (
      <motion.div
        layoutId="sidebar-active"
        className="absolute left-0 top-0 w-1 h-full bg-orange-500 rounded-r"
      />
    )}
    {icon}
    <span>{label}</span>
  </motion.button>
);

// Collapsed icon style
const CollapsedIcon = ({ icon, title, onClick, active }) => (
  <motion.button
    whileHover={{ scale: 1.15 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    title={title}
    className={`
      p-2 rounded-lg transition 
      ${active ? "bg-orange-600 text-white shadow-md" : "text-gray-700 hover:bg-gray-100"}
    `}
  >
    {icon}
  </motion.button>
);

export default Sidebar;
